import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
	Alert,
	Backdrop,
	Button,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Snackbar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Box,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import { deleteCostIssuingRule, getAllProviders, getCostIssuingRulesList } from '../../services';
import { IssueCostCondition } from '../../types';
import { describeRule, describeRuleWriteError, isRuleGone } from '../../helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { defaultAlertState } from 'features/common/constants';
import { IProviderListDTO } from 'features/providers/types';
import { CostIssuingRuleDialog } from '../CostIssuingRuleDialog';
import { CopyCostIssuingRulesDialog } from '../CopyCostIssuingRulesDialog';

export const CostIssuingRulesList: React.FC = () => {
	const [rules, setRules] = useState<IssueCostCondition[]>([]);
	const [providers, setProviders] = useState<IProviderListDTO[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const canEdit = isAdminOrSuperAdmin(useSelector((state: RootState) => state.auth.user));
	// El diálogo de alta o edición: `rule` es la regla a editar, `null` un alta.
	const [ruleDialog, setRuleDialog] = useState<{ open: boolean; rule: IssueCostCondition | null }>({
		open: false,
		rule: null,
	});
	const [copyOpen, setCopyOpen] = useState<boolean>(false);
	const [ruleToDelete, setRuleToDelete] = useState<IssueCostCondition | null>(null);
	const [deleting, setDeleting] = useState<boolean>(false);
	// Sin todos los proveedores no se crea, edita ni duplica: el diálogo no encontraría el de la regla.
	const [providersComplete, setProvidersComplete] = useState<boolean>(false);
	// La regla que se está borrando, para que el diálogo no quede vacío mientras se cierra.
	const lastToDelete = useRef<IssueCostCondition | null>(null);
	if (ruleToDelete) lastToDelete.current = ruleToDelete;
	const shownToDelete = ruleToDelete ?? lastToDelete.current;

	// Estados para filtros
	const [selectedProviderId, setSelectedProviderId] = useState<string>('');
	const [selectedValidatingCarrier, setSelectedValidatingCarrier] = useState<string>('');

	useEffect(() => {
		loadRules();
		loadProviders();
	}, []);

	const loadRules = async () => {
		try {
			setLoading(true);
			const response = await getCostIssuingRulesList();
			setRules(response.data || []);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Error al cargar las reglas de emisión de costos'),
			});
		} finally {
			setLoading(false);
		}
	};

	const loadProviders = async () => {
		const warn = () =>
			setAlert({
				show: true,
				severity: 'warning',
				message: canEdit
					? 'No se pudieron cargar los proveedores: algunas reglas muestran el id y no se puede crear, editar ni duplicar.'
					: 'No se pudieron cargar los proveedores: algunas reglas muestran el id.',
			});
		try {
			const { providers: loaded, complete } = await getAllProviders();
			setProviders(loaded);
			setProvidersComplete(complete);
			if (!complete) warn();
		} catch (error: any) {
			console.error('Error loading providers:', error);
			warn();
		}
	};

	const getProviderName = (providerId: string) => {
		const provider = providers.find((p) => p.id === providerId);
		return provider ? provider.fantasyName : providerId;
	};

	// Obtener valores únicos de Validating Carrier
	const validatingCarriers = useMemo(() => {
		const carriers = rules.map((rule) => rule.validatingCarrier);
		return Array.from(new Set(carriers)).sort();
	}, [rules]);

	// Filtrar reglas basándose en los filtros seleccionados
	const filteredRules = useMemo(() => {
		return rules.filter((rule) => {
			const providerMatch = !selectedProviderId || rule.providerId === selectedProviderId;
			const carrierMatch = !selectedValidatingCarrier || rule.validatingCarrier === selectedValidatingCarrier;
			return providerMatch && carrierMatch;
		});
	}, [rules, selectedProviderId, selectedValidatingCarrier]);

	const handleProviderFilterChange = (event: any) => {
		setSelectedProviderId(event.target.value);
	};

	const handleValidatingCarrierFilterChange = (event: any) => {
		setSelectedValidatingCarrier(event.target.value);
	};

	const clearFilters = () => {
		setSelectedProviderId('');
		setSelectedValidatingCarrier('');
	};

	const handleSnackbarClose = () => {
		setAlert(defaultAlertState);
	};

	// Una API de sale orders anterior al ABM no manda el id: la regla se puede mirar, pero no editar ni borrar.
	const withoutIds = rules.length > 0 && rules.some((rule) => rule.id === undefined);

	const handleSaved = (message: string) => {
		setRuleDialog({ open: false, rule: null });
		setCopyOpen(false);
		setAlert({ show: true, severity: 'success', message });
		loadRules();
	};

	// La regla ya no existe (otro la borró): se cierra lo abierto y se actualiza la lista.
	const handleGone = (message: string) => {
		setRuleDialog({ open: false, rule: null });
		setRuleToDelete(null);
		setAlert({ show: true, severity: 'warning', message });
		loadRules();
	};

	const handleConfirmDelete = async () => {
		if (ruleToDelete?.id === undefined) return;
		setDeleting(true);
		try {
			await deleteCostIssuingRule(ruleToDelete.id);
			setRuleToDelete(null);
			setAlert({ show: true, severity: 'success', message: 'Regla eliminada' });
			loadRules();
		} catch (error) {
			const message = describeRuleWriteError(error, 'No se pudo eliminar');
			if (isRuleGone(error)) handleGone(message);
			else setAlert({ show: true, severity: 'error', message });
		} finally {
			setDeleting(false);
		}
	};

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('es-UY', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
		}).format(value);
	};

	const formatPercentage = (value: number) => {
		return `${(value * 100).toFixed(2)}%`;
	};

	return (
		<>
			<Backdrop open={loading} style={{ zIndex: 9999 }}>
				<CircularProgress color="inherit" />
			</Backdrop>

			{canEdit && (
				<Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={() => setRuleDialog({ open: true, rule: null })}
						disabled={withoutIds || !providersComplete}
						size="large"
					>
						Nueva Regla
					</Button>
					<Button
						variant="outlined"
						startIcon={<ContentCopyIcon />}
						onClick={() => setCopyOpen(true)}
						disabled={withoutIds || !providersComplete}
						size="large"
					>
						Duplicar reglas
					</Button>
				</Box>
			)}

			{canEdit && withoutIds && (
				<Alert severity="info" sx={{ mb: 2 }}>
					La API de sale orders todavía no tiene la versión que permite editar: por ahora las reglas solo se pueden
					mirar.
				</Alert>
			)}

			{/* Filtros */}
			<Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="h6" sx={{ mb: 2 }}>
					Filtros
				</Typography>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={4}>
						<FormControl fullWidth>
							<InputLabel>Proveedor</InputLabel>
							<Select value={selectedProviderId} onChange={handleProviderFilterChange} label="Proveedor">
								<MenuItem value="">
									<em>Todos los proveedores</em>
								</MenuItem>
								{providers.map((provider) => (
									<MenuItem key={provider.id} value={provider.id}>
										{provider.fantasyName}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={4}>
						<FormControl fullWidth>
							<InputLabel>Validating Carrier</InputLabel>
							<Select
								value={selectedValidatingCarrier}
								onChange={handleValidatingCarrierFilterChange}
								label="Validating Carrier"
							>
								<MenuItem value="">
									<em>Todos los carriers</em>
								</MenuItem>
								{validatingCarriers.map((carrier) => (
									<MenuItem key={carrier} value={carrier}>
										{carrier}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Button
							variant="outlined"
							onClick={clearFilters}
							disabled={!selectedProviderId && !selectedValidatingCarrier}
						>
							Limpiar Filtros
						</Button>
					</Grid>
				</Grid>
			</Paper>

			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>
								<strong>Proveedor</strong>
							</TableCell>
							<TableCell>
								<strong>Validating Carrier</strong>
							</TableCell>
							<TableCell>
								<strong>Tipo de Tarifa</strong>
							</TableCell>
							<TableCell align="right">
								<strong>Comisión</strong>
							</TableCell>
							<TableCell align="right">
								<strong>Over</strong>
							</TableCell>
							<TableCell align="right">
								<strong>Fee %</strong>
							</TableCell>
							<TableCell align="right">
								<strong>Fee Valor</strong>
							</TableCell>
							<TableCell align="center">
								<strong>Mínimo</strong>
							</TableCell>
							{canEdit && (
								<TableCell align="center">
									<strong>Acciones</strong>
								</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredRules.length === 0 ? (
							<TableRow>
								<TableCell colSpan={canEdit ? 9 : 8} align="center">
									<Typography variant="body1" color="text.secondary">
										{rules.length === 0
											? 'No hay reglas de emisión de costos configuradas'
											: 'No hay reglas que coincidan con los filtros seleccionados'}
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							filteredRules.map((rule, index) => (
								<TableRow key={rule.id ?? `sin-id-${index}`} hover>
									<TableCell>{getProviderName(rule.providerId)}</TableCell>
									<TableCell>
										<Chip
											label={rule.validatingCarrier}
											color={
												rule.validatingCarrier === 'ALL'
													? 'primary'
													: rule.validatingCarrier === 'REST'
													? 'secondary'
													: 'default'
											}
											size="small"
										/>
									</TableCell>
									<TableCell>{rule.tariffType}</TableCell>
									<TableCell align="right">{formatPercentage(rule.commission)}</TableCell>
									<TableCell align="right">{formatPercentage(rule.over)}</TableCell>
									<TableCell align="right">{formatPercentage(rule.feePercentage)}</TableCell>
									<TableCell align="right">{formatCurrency(rule.feeValue)}</TableCell>
									<TableCell align="center">
										<Chip
											label={rule.applyMinimum ? 'Sí' : 'No'}
											color={rule.applyMinimum ? 'success' : 'default'}
											size="small"
										/>
									</TableCell>
									{canEdit && (
										<TableCell align="center">
											<Button
												size="small"
												startIcon={<EditIcon />}
												onClick={() => setRuleDialog({ open: true, rule })}
												disabled={rule.id === undefined || !providersComplete}
												sx={{ mr: 1 }}
											>
												Editar
											</Button>
											<Button
												size="small"
												color="error"
												startIcon={<DeleteIcon />}
												onClick={() => setRuleToDelete(rule)}
												disabled={rule.id === undefined}
											>
												Eliminar
											</Button>
										</TableCell>
									)}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{ruleDialog.open && (
				<CostIssuingRuleDialog
					rule={ruleDialog.rule}
					providers={providers}
					onClose={() => setRuleDialog({ open: false, rule: null })}
					onSaved={handleSaved}
					onGone={handleGone}
				/>
			)}

			{copyOpen && (
				<CopyCostIssuingRulesDialog
					rules={rules}
					providers={providers}
					onClose={() => setCopyOpen(false)}
					onCopied={handleSaved}
				/>
			)}

			<Dialog open={ruleToDelete !== null} onClose={deleting ? undefined : () => setRuleToDelete(null)}>
				<DialogTitle>¿Eliminar la regla?</DialogTitle>
				<DialogContent>
					{shownToDelete && (
						<Typography>
							{getProviderName(shownToDelete.providerId)}: {describeRule(shownToDelete)}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRuleToDelete(null)} disabled={deleting}>
						Cancelar
					</Button>
					<Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar para alertas */}
			<Snackbar
				open={alert.show}
				autoHideDuration={6000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={handleSnackbarClose} severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
